import { useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"

import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { useProductImageZoom } from "scrivito/Objs/Product/Components/useProductImageZoom"
import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import { NOT_FOUND } from "shared/constants/permalinks"
import {
  NEW_PRODUCT_POSITION_OVERLAY,
  PRODUCT_CONFIGURATION_OVERLAY
} from "shared/context/overlay/types"
import { isConfigurationEqual } from "shared/lib/product-configuration"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { useTitle } from "shared/lib/usePageTitle"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"

import { useBuildingReferenceFilter } from "entities/building-references/model/useBuildingReferenceFilter"
import {
  useExternalDimensioningResultLazyQuery,
  useExternalDimensioningResultQuery
} from "entities/dim-configuration/graphql/external-dimensioning.queries.generated"
import { getProductInformationFiles } from "entities/products/lib/getProductInformationFiles"
import { getProductInformationQuickInfos } from "entities/products/lib/getProductInformationQuickInfos"
import { getProductType } from "entities/products/lib/getProductType"
import { useAggregatedProductData } from "entities/products/model/useAggregatedProductData"
import { useExternalDimensioningModelAvailability } from "entities/products/model/useExternalDimensioningModelAvailability"
import { useNavigateToProduct } from "entities/products/model/useNavigateToProduct"
import { useProductConfigurationModelAvailability } from "entities/products/model/useProductConfigurationModelAvailability"
import { useProductObj } from "entities/products/model/useProductObj"
import { useFavorite } from "entities/projects/api/useFavorite"
import { useProjects } from "entities/projects/api/useProjects"
import { useNavigateToPermalink } from "features/navigation/model/useNavigateToPermalink"
import { useScrollController } from "features/scroll-management/model/useScrollController"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"

import { useProductPageSearchParams } from "../lib/useProductPageSearchParams"
import { ProductPageModel } from "../types/ProductPageModel"

export function useProductPageModel({
  productType
}: {
  productType: string
}): ProductPageModel {
  useScrollController()
  const { navigate } = useNavigateToPermalink()
  const { pathname } = useLocation()
  const [fetchExternalDimensioning] = useExternalDimensioningResultLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const configuration = useProductPageSearchParams({ productType })
  const { variantKey, configurationKey, dimensioningKey } = configuration

  const { isFavorite, toggleFavorite } = useFavorite(configuration)

  const productData = useAggregatedProductData(configuration)
  const { productInformation } = productData
  const { openOverlay: openProductConfigurationOverlay } = useOverlay(
    PRODUCT_CONFIGURATION_OVERLAY
  )

  const { isAuthenticated } = useAuth()
  const { activeProjectID } = useNeoletterUserProfile()
  const { projects: isProjectsServiceAvailable } = useAPIHealth()
  const { loading: isProjectsLoading } = useProjects()
  const { openOverlay: openAddToProjectOverlay } = useOverlay(
    NEW_PRODUCT_POSITION_OVERLAY
  )

  const { data } = useExternalDimensioningResultQuery({
    variables: { id: dimensioningKey as string },
    skip: !dimensioningKey
  })

  const handleAddToProject = useEvent(() => {
    if (!activeProjectID) return
    openAddToProjectOverlay({
      projectID: activeProjectID,
      defaultValues: {
        quantity: 1,
        variantKey,
        configurationKey,
        dimensioning: data?.externalDimensioningResult
      }
    })
  })

  const onAddToProject =
    isAuthenticated && !isProjectsLoading && isProjectsServiceAvailable
      ? handleAddToProject
      : undefined

  const { productObj, productBaseVariantObj } = useProductObj(variantKey)
  useTitle(productObj?.get("title") || productType)

  const onZoomProductImage = useProductImageZoom(
    productBaseVariantObj || productObj
  )

  const navigateToProduct = useNavigateToProduct(null)
  const handleConfigurationChange = useEvent(
    (nextConfiguration: {
      variantKey: string
      configurationKey?: string | null
      dimensioningKey?: string | null
    }) => {
      if (pathname.includes(getProductType(configuration.variantKey))) {
        navigateToProduct(nextConfiguration, { replace: true })
      }
    }
  )
  const handleConfiguratorResult = useEvent(
    (nextConfiguration: ProductConfiguratorResult) => {
      handleConfigurationChange({
        ...nextConfiguration,
        dimensioningKey:
          nextConfiguration.externalDimensioningResult?.transactionId
      })
    }
  )

  const handleDimension = useEvent(() => {
    openProductConfigurationOverlay({
      configuration: {
        ...configuration,
        externalDimensioningResult:
          data?.externalDimensioningResult ?? undefined
      },
      mode: ProductConfiguratorMode.DIMENSIONING,
      onSubmit: handleConfiguratorResult
    })
  })

  const handleConfigure = useEvent(() => {
    openProductConfigurationOverlay({
      configuration,
      mode: ProductConfiguratorMode.CONFIGURATION,
      onSubmit: handleConfiguratorResult
    })
  })

  const handleDownloadPDF = useEvent(() => {
    window.print()
  })

  useEffect(() => {
    if (productInformation?.data?.variantKey) {
      const nextConfiguration = {
        variantKey: productInformation.data.variantKey,
        configurationKey: productInformation.data.configurationKey,
        dimensioningKey
      }
      if (!isConfigurationEqual(configuration, nextConfiguration)) {
        handleConfigurationChange(nextConfiguration)
      }
    }
  }, [
    productInformation?.data?.variantKey,
    productInformation?.data?.configurationKey,
    dimensioningKey
  ])

  useEffect(() => {
    if (dimensioningKey) {
      fetchExternalDimensioning({
        variables: { id: dimensioningKey as string }
      })
        .then(result => {
          if (!result.data?.externalDimensioningResult?.transactionId) {
            handleConfigurationChange({ variantKey, configurationKey })
          }
        })
        .catch(() => {
          handleConfigurationChange({ variantKey, configurationKey })
        })
    }
  }, [dimensioningKey])

  useEffect(() => {
    if (
      !productObj &&
      !productInformation?.data &&
      !productInformation?.isLoading
    ) {
      navigate(NOT_FOUND)
    }
  }, [productObj, productInformation])

  const { isConfigurationAvailable } =
    useProductConfigurationModelAvailability(productType)
  const { isExternalDimensioningAvailable } =
    useExternalDimensioningModelAvailability(productType)
  const buildingReferenceFilterState = useMemo(
    () => ({ products: [productType] }),
    [productType]
  )
  const automaticBuildingReferences = useBuildingReferenceFilter(
    buildingReferenceFilterState
  )
  const buildingReferenceObjs = productObj?.get("automaticBuildingReferences")
    ? automaticBuildingReferences.buildingReferenceObjs
    : (productObj?.get("references") as BuildingReferenceObj[] | undefined)

  return {
    productType,
    variantKey,

    productData,
    productObj,
    productBaseVariantObj,

    isFavorited: isFavorite,
    buildingReferenceObjs,

    isAuthenticated,

    getFiles: getProductInformationFiles,
    getQuickInfos: getProductInformationQuickInfos,

    onToggleFavorite: toggleFavorite,
    onConfigure: isConfigurationAvailable ? handleConfigure : undefined,
    onDimension: isExternalDimensioningAvailable ? handleDimension : undefined,
    onDownloadPDF: handleDownloadPDF,
    onZoomProductImage,
    onAddToProject
  }
}
